import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

// Context Providers
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CompareProvider } from "./context/CompareContext";

// Layout Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import StickyCart from "./components/StickyCart";
import { CompareBar, ComparePage } from "./components/ProductCompare";
import CookieConsent from "./components/CookieConsent";
import MaintenanceMode from "./components/MaintenanceMode";

// Maintenance Mode: controlled dynamically from Admin panel (Табло → Режим на поддръжка)
// Secret key to bypass maintenance mode: add ?admin_access=vt-Du2iypTw73 to URL
const MAINTENANCE_BYPASS_KEY = "vt-Du2iypTw73";

// Pages
import HomePage from "./pages/HomePage";
import PromotionsPage from "./pages/PromotionsPage";
import OfferPage from "./pages/OfferPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import ProfilePage from "./pages/ProfilePage";
import OrdersPage from "./pages/OrdersPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import ReturnsPage from "./pages/ReturnsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AdminPage from "./pages/AdminPage";
import FAQPage from "./pages/FAQPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }

    return children;
};

// Layout wrapper for pages with header/footer
const MainLayout = ({ children }) => {
    return (
        <>
            <Header />
            <main>{children}</main>
            <Footer />
        </>
    );
};

// Scroll to top on route change
const ScrollToTop = () => {
    const { pathname } = useLocation();
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    
    return null;
};

// App Router Component
const AppRouter = () => {
    const location = useLocation();

    // Check URL fragment for session_id from OAuth callback
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <>
            <ScrollToTop />
            <Routes>
                {/* Public routes with layout */}
                <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
                <Route path="/products" element={<MainLayout><ProductsPage /></MainLayout>} />
                <Route path="/product/:productId" element={<MainLayout><ProductDetailPage /></MainLayout>} />
                <Route path="/cart" element={<MainLayout><CartPage /></MainLayout>} />
                <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
                <Route path="/faq" element={<MainLayout><FAQPage /></MainLayout>} />
                <Route path="/terms" element={<MainLayout><TermsPage /></MainLayout>} />
                <Route path="/returns" element={<MainLayout><ReturnsPage /></MainLayout>} />
                <Route path="/privacy" element={<MainLayout><PrivacyPage /></MainLayout>} />
                
                {/* Product comparison */}
                <Route path="/compare" element={<MainLayout><ComparePage /></MainLayout>} />
                
                {/* Order tracking - public */}
                <Route path="/track" element={<MainLayout><OrderTrackingPage /></MainLayout>} />
                <Route path="/track/:orderId" element={<MainLayout><OrderTrackingPage /></MainLayout>} />

                {/* Promotions - public */}
                <Route path="/promotions" element={<MainLayout><PromotionsPage /></MainLayout>} />
                <Route path="/offer/:ref" element={<MainLayout><OfferPage /></MainLayout>} />

                {/* Auth routes without layout */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Checkout - allows guest checkout */}
                <Route path="/checkout" element={<MainLayout><CheckoutPage /></MainLayout>} />
                <Route path="/order-success" element={<MainLayout><OrderSuccessPage /></MainLayout>} />
                
                {/* Protected routes - require login */}
                <Route path="/profile" element={
                    <ProtectedRoute>
                        <MainLayout><ProfilePage /></MainLayout>
                    </ProtectedRoute>
                } />
                <Route path="/orders" element={
                    <ProtectedRoute>
                        <MainLayout><OrdersPage /></MainLayout>
                    </ProtectedRoute>
                } />
                <Route path="/orders/:orderId" element={
                    <ProtectedRoute>
                        <MainLayout><OrdersPage /></MainLayout>
                    </ProtectedRoute>
                } />

                {/* Admin routes */}
                <Route path="/admin/*" element={
                    <ProtectedRoute>
                        <AdminPage />
                    </ProtectedRoute>
                } />

                {/* 404 */}
                <Route path="*" element={
                    <MainLayout>
                        <div className="min-h-screen flex items-center justify-center">
                            <div className="text-center">
                                <h1 className="font-serif text-6xl font-bold text-primary mb-4">404</h1>
                                <p className="text-muted-foreground mb-8">Страницата не е намерена</p>
                                <a href="/" className="btn-primary inline-block px-8 py-3 rounded-md">
                                    Към началната страница
                                </a>
                            </div>
                        </div>
                    </MainLayout>
                } />
            </Routes>
        </>
    );
};

function App() {
    const [bypassMaintenance, setBypassMaintenance] = useState(false);
    const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);

    useEffect(() => {
        // Check for bypass key in URL or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const adminAccess = urlParams.get('admin_access');
        
        if (adminAccess === MAINTENANCE_BYPASS_KEY) {
            localStorage.setItem('maintenance_bypass', 'true');
            setBypassMaintenance(true);
        } else if (localStorage.getItem('maintenance_bypass') === 'true') {
            setBypassMaintenance(true);
        }
    }, []);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance`)
            .then(res => setMaintenanceEnabled(!!res.data.enabled))
            .catch(() => setMaintenanceEnabled(false));
    }, []);

    // Admin panel and login stay accessible so the mode can be turned off
    const path = window.location.pathname;
    const isAdminPath = path.startsWith('/admin') || path === '/login' || path.startsWith('/auth');

    // Show maintenance page if enabled and not bypassed
    if (maintenanceEnabled && !bypassMaintenance && !isAdminPath) {
        return <MaintenanceMode />;
    }

    return (
        <div className="App">
            <BrowserRouter>
                <ThemeProvider>
                    <AuthProvider>
                        <CartProvider>
                            <CompareProvider>
                                <AppRouter />
                                <Toaster position="top-right" richColors />
                                <PWAInstallPrompt />
                                <StickyCart />
                                <CompareBar />
                                <CookieConsent />
                            </CompareProvider>
                        </CartProvider>
                    </AuthProvider>
                </ThemeProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
